import axios from 'axios';

import { BFF_BASE_URL } from '../auth/authApi';

/** The four automatic checks (backend agent/reply_checks.py). true = failed, false = passed, null = could not tell. */
export type Checks = Partial<Record<CheckName, boolean | null>>;

export type CheckName =
    | 'language_mismatch'
    | 'claimed_change_without_tool'
    | 'promised_future_action'
    | 'empty_or_failed';

export const CHECK_LABELS: Record<CheckName, string> = {
    language_mismatch: 'Replied in the other language',
    claimed_change_without_tool: 'Said it changed something, no tool ran',
    promised_future_action: 'Promised an action it cannot take later',
    empty_or_failed: 'Empty reply',
};

export const CHECK_NAMES = Object.keys(CHECK_LABELS) as CheckName[];

/** The names of the checks that failed, in a stable order. */
export const failedChecks = (checks: Checks | null | undefined): CheckName[] =>
    CHECK_NAMES.filter((name) => checks?.[name] === true);

/** Whether at least one check could give an answer (a reply with none was never graded). */
export const wasChecked = (checks: Checks | null | undefined): boolean =>
    CHECK_NAMES.some((name) => checks?.[name] === true || checks?.[name] === false);

/** The thumbs-down categories of the chat UI's feedback modal (ui/.../FeedbackModal.tsx). */
export const CATEGORY_LABELS: Record<string, string> = {
    hallucinated: 'Made up information',
    wrong: 'Factually incorrect',
    irrelevant: 'Off-topic / not helpful',
    incomplete: 'Incomplete answer',
    no_tool: 'Did not call a tool',
    wrong_tool: 'Called the wrong tool',
    too_long: 'Too verbose',
    formatting: 'Formatting / readability',
    other: 'Other',
    uncategorised: 'No category given',
};

export const categoryLabel = (category: string): string => CATEGORY_LABELS[category] ?? category;

export interface Tally {
    replies: number;
    rated: number;
    up: number;
    down: number;
    satisfaction: number | null;
    /** Lower bound of the 95% Wilson interval; what the ranking uses. */
    score: number | null;
    /** Enough rated replies to trust the ranking. */
    enough: boolean;
    checked: number;
    check_failed: number;
    check_failed_rate: number | null;
    tool_call_rate: number | null;
    guard_known: number;
    guard_fired: number;
    guard_rate: number | null;
}

export interface Headline extends Tally {
    failed_messages: number;
    coverage: number | null;
}

export type ModelRow = Tally & { model: string };
export type VersionRow = Tally & { version: number | null };
export type PairRow = Tally & { model: string; version: number | null };

export interface DownReply {
    id: string;
    user_id: string;
    timestamp: string;
    model: string;
    prompt_version: number | null;
    category: string | null;
    reason: string;
    failed_checks: CheckName[];
    user_text: string;
    reply: string;
}

export interface Causes {
    down_total: number;
    categories: { category: string; count: number; share: number }[];
    checks: {
        check: CheckName;
        down_failed: number;
        down_evaluated: number;
        all_failed: number;
        all_evaluated: number;
    }[];
    replies: DownReply[];
}

export interface TrendPoint {
    week: string;
    up: number;
    down: number;
    rated: number;
    satisfaction: number | null;
}

export interface ReplayGroup {
    runs: number;
    done: number;
    failed: number;
    verdicts: { better: number; same: number; worse: number; unjudged: number };
    checked: number;
    check_failed: number;
    check_failed_rate: number | null;
    guard_fired: number;
    tried_to_write: number;
    avg_duration_ms: number | null;
    /** Of the runs that re-answered a reply the user gave a thumbs-down. */
    original_down: { runs: number; better: number; same: number; worse: number; unjudged: number; clean: number };
}

export interface QualityReport {
    window: { days: number; since: string | null; user_id: string | null; truncated: boolean };
    headline: Headline;
    by_model: ModelRow[];
    by_prompt_version: VersionRow[];
    by_model_prompt: PairRow[];
    causes: Causes;
    trend: TrendPoint[];
    replays: {
        by_model: (ReplayGroup & { model: string })[];
        batches: (ReplayGroup & {
            id: string;
            model: string;
            prompt_source: 'recorded' | 'current';
            criteria: { feedback: 'up' | 'down' | null } | null;
            created_at: string;
        })[];
    };
}

export const getQuality = async (days: number, userId?: string): Promise<QualityReport> => {
    const response = await axios.get<QualityReport>(`${BFF_BASE_URL}/admin/quality`, {
        params: { days, user_id: userId || undefined },
    });
    return response.data;
};
